import React, { useState, useEffect, useRef } from 'react';
import { FiPlusCircle, FiTrash2, FiEye, FiDownload, FiChevronDown, FiSearch } from 'react-icons/fi';
import { getRequisitions, deleteRequisition } from '../../api/procurementApi';
import RequisitionFormModal from '../../components/RequisitionFormModal';
import ViewRequisitionModal, { ViewRequisitionContent } from '../../components/programManager/ViewRequisitionModal';
import DeleteConfirmModal from '../../components/programManager/DeleteConfirmModal';
import MasterSideBar from "../../components/MasterSideBar";
import Topbar from "../../components/TopbarNav";
import { toast } from 'react-toastify';
import Select from 'react-select';
import { Pencil, FileText, Filter } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const DEPARTMENT_OPTIONS = [
    { value: '', label: 'All Departments' },
    { value: 'operations', label: 'Operations' },
    { value: 'finance', label: 'Finance' },
    { value: 'hr', label: 'HR' },
    { value: 'it', label: 'IT' },
    { value: 'logistics', label: 'Logistics' },
    { value: 'admin', label: 'Administration' },
];

const REQUISITION_STATUS_FILTER_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'pending_director_approval', label: 'Awaiting Director' },
    { value: 'approved_by_director', label: 'Approved' },
    { value: 'rejected_by_director', label: 'Rejected' },
    { value: 'completed', label: 'Completed' },
];

const PROCUREMENT_TYPE_FILTER_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'goods_services', label: 'Goods & Services' },
    { value: 'financial_request', label: 'Financial Request' },
    { value: 'travel', label: 'Travel Request' },
];

const OperationsAssociateRequisitions = () => {
    const [requisitions, setRequisitions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [requisitionToDelete, setRequisitionToDelete] = useState(null);
    const [requisitionToEdit, setRequisitionToEdit] = useState(null);
    const [requisitionToView, setRequisitionToView] = useState(null);

    const [selectedStatusFilter, setSelectedStatusFilter] = useState(REQUISITION_STATUS_FILTER_OPTIONS[0]);
    const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState(DEPARTMENT_OPTIONS[0]);
    const [selectedTypeFilter, setSelectedTypeFilter] = useState(PROCUREMENT_TYPE_FILTER_OPTIONS[0]);
    const [searchQuery, setSearchQuery] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const REQUISITIONS_PER_PAGE = 10;
    
    // Sidebar States
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const fetchRequisitions = async () => {
        setLoading(true);
        try {
            const filters = {
                status: selectedStatusFilter.value,
                department: selectedDepartmentFilter.value,
                typeOfProcurement: selectedTypeFilter.value,
                search: searchQuery,
                page: currentPage,
                limit: REQUISITIONS_PER_PAGE,
            };
            const response = await getRequisitions(filters);
            setRequisitions(response.data?.data || []);
            setTotalCount(response.data?.totalCount || 0);
            setTotalPages(response.data?.totalPages || 1);
        } catch (err) {
            setError("Failed to synchronize with requisition database.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchRequisitions(); }, [currentPage, selectedStatusFilter, selectedDepartmentFilter, selectedTypeFilter, searchQuery]);

    const handleEditRequisition = (requisition) => {
        if (['draft', 'rejected_by_director'].includes(requisition.currentStatus)) {
            setRequisitionToEdit(requisition);
            setIsCreateModalOpen(true);
        } else {
            toast.info("Active requisitions cannot be modified.");
        }
    };

    // Export Logic
    const handleExport = (format) => {
        setExportDropdownOpen(false);
        const exportData = requisitions.map(req => ({
            "Title": req.title,
            "Project": req.projectId?.projectTitle || 'N/A',
            "Type": req.typeOfProcurement,
            "Status": req.currentStatus,
            "Due Date": new Date(req.dueDate).toLocaleDateString()
        }));

        if (format === "excel") {
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Requisitions");
            XLSX.writeFile(wb, "requisition_report.xlsx");
        } else if (format === "pdf") {
            const doc = new jsPDF("l", "pt", "a4");
            autoTable(doc, { head: [Object.keys(exportData[0])], body: exportData.map(row => Object.values(row)) });
            doc.save("requisition_report.pdf");
        }
    };

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            backgroundColor: '#F9FAFB',
            border: 'none',
            borderRadius: '0.75rem',
            padding: '2px',
            fontSize: '12px',
            fontWeight: '700',
            boxShadow: 'none'
        })
    };

    return (
        <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
            <MasterSideBar 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)} 
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <Topbar onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-7xl mx-auto space-y-6">
                        
                        {/* Header Action Bar */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase">
                                    Requisition <span className="text-orange-600">Central</span>
                                </h1>
                                <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mt-1 italic">Internal Needs & Resource Allocation</p>
                            </div>
                            <div className="flex gap-2">
                                <div className="relative" ref={dropdownRef}>
                                    <button 
                                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                                        className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all"
                                    >
                                        <FiDownload /> Export <FiChevronDown />
                                    </button>
                                    <AnimatePresence>
                                        {exportDropdownOpen && (
                                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute right-0 mt-2 w-40 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 py-2">
                                                {['excel', 'pdf'].map(fmt => (
                                                    <button key={fmt} onClick={() => handleExport(fmt)} className="w-full text-left px-5 py-2 text-[10px] font-black text-gray-600 hover:bg-orange-50 hover:text-orange-600 uppercase">{fmt}</button>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                                <button
                                    onClick={() => setIsCreateModalOpen(true)}
                                    className="flex items-center gap-2 bg-orange-600 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-500 transition-all shadow-lg shadow-orange-100"
                                >
                                    <FiPlusCircle /> New Requisition
                                </button>
                            </div>
                        </div>

                        {/* Filter Control Center */}
                        <section className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 md:p-8">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2"><Filter size={12}/> Status</label>
                                    <Select styles={customSelectStyles} options={REQUISITION_STATUS_FILTER_OPTIONS} value={selectedStatusFilter} onChange={setSelectedStatusFilter} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2"><Filter size={12}/> Department</label>
                                    <Select styles={customSelectStyles} options={DEPARTMENT_OPTIONS} value={selectedDepartmentFilter} onChange={setSelectedDepartmentFilter} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2"><Filter size={12}/> Category</label>
                                    <Select styles={customSelectStyles} options={PROCUREMENT_TYPE_FILTER_OPTIONS} value={selectedTypeFilter} onChange={setSelectedTypeFilter} />
                                </div>
                                <div className="relative">
                                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        placeholder="SEARCH TRACKING ID..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl text-[10px] font-black focus:ring-2 focus:ring-orange-500 uppercase tracking-widest"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Main Requisition Table */}
                        <section className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50/50">
                                            <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Requisition Brief</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Ownership</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status Matrix</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Timeline</th>
                                            <th className="px-8 py-5 text-right"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {loading ? (
                                            <tr><td colSpan="5" className="text-center py-20 text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">Syncing Requisition Data...</td></tr>
                                        ) : requisitions.map((req) => (
                                            <tr key={req._id} className="hover:bg-gray-50/50 transition-colors group">
                                                <td className="px-8 py-5">
                                                    <p className="text-sm font-black text-gray-800 uppercase leading-tight">{req.title}</p>
                                                    <p className="text-[9px] font-black text-orange-600 mt-1 uppercase tracking-tighter italic">
                                                        {req.projectId?.projectTitle || 'General Overhead'}
                                                    </p>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <p className="text-xs font-bold text-gray-700">{req.requisitionBy?.firstName} {req.requisitionBy?.lastName}</p>
                                                    <div className="flex gap-1 mt-1">
                                                        {req.departmentsTargeted.map(dept => (
                                                            <span key={dept} className="text-[8px] font-black bg-gray-100 px-2 py-0.5 rounded text-gray-500 uppercase">{dept}</span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <span className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-tighter ${
                                                        req.currentStatus === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                                                        req.currentStatus === 'rejected_by_director' ? 'bg-red-50 text-red-600' :
                                                        'bg-orange-50 text-orange-600'
                                                    }`}>
                                                        {req.currentStatus.replace(/_/g, ' ')}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">Due Date</p>
                                                    <p className="text-xs font-bold text-gray-800">{new Date(req.dueDate).toLocaleDateString()}</p>
                                                </td>
                                                <td className="px-8 py-5 text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <button onClick={() => { setRequisitionToView(req); setIsViewModalOpen(true); }} className="p-2 text-gray-300 hover:text-gray-900 transition-colors"><FiEye size={18}/></button>
                                                        <button onClick={() => handleEditRequisition(req)} className="p-2 text-gray-300 hover:text-orange-600 transition-colors"><Pencil size={16}/></button>
                                                        <button onClick={() => { setRequisitionToDelete(req); setIsDeleteModalOpen(true); }} className="p-2 text-gray-300 hover:text-red-600 transition-colors"><FiTrash2 size={18}/></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="p-6 bg-gray-50/50 border-t border-gray-50 flex justify-between items-center">
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Entries: {totalCount}</p>
                                <div className="flex gap-1">
                                    {Array.from({ length: totalPages }).map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setCurrentPage(i + 1)}
                                            className={`w-8 h-8 rounded-lg text-[10px] font-black transition-all ${
                                                currentPage === i + 1 ? 'bg-orange-600 text-white shadow-md' : 'bg-white text-gray-400 hover:bg-gray-100'
                                            }`}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </section>
                    </div>
                </main>
            </div>

            {/* Modal Components - Retaining existing business logic */}
            <RequisitionFormModal
                isOpen={isCreateModalOpen}
                onClose={() => { setIsCreateModalOpen(false); setRequisitionToEdit(null); }}
                onSaveSuccess={() => { fetchRequisitions(); setIsCreateModalOpen(false); }}
                requisitionToEdit={requisitionToEdit}
            />

            <AnimatePresence>
                {isViewModalOpen && requisitionToView && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }} 
                            exit={{ opacity: 0 }} 
                            onClick={() => setIsViewModalOpen(false)} 
                            className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
                        />
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 20 }} 
                            animate={{ scale: 1, opacity: 1, y: 0 }} 
                            exit={{ scale: 0.95, opacity: 0, y: 20 }} 
                            className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-100"
                        >
                            <div className="p-0 overflow-y-auto max-h-full">
                                <ViewRequisitionContent
                                    requisition={requisitionToView}
                                    onRequestClose={() => setIsViewModalOpen(false)}
                                />
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onRequestClose={() => setIsDeleteModalOpen(false)}
                onConfirm={async () => {
                    await deleteRequisition(requisitionToDelete._id);
                    toast.success("Record Purged");
                    fetchRequisitions();
                    setIsDeleteModalOpen(false);
                }}
                itemType="requisition"
            />
        </div>
    );
};

export default OperationsAssociateRequisitions;